// Settings — Team Members + Locations + Password change API
// (Settings-01 / Settings-01b / Settings-02).

import { api } from "@/api/client";

import type {
  AcceptInvitationInput,
  BeneAIAgentSummary,
  BrokerLocation,
  BrokerLocationCreateInput,
  BrokerLocationListResponse,
  BrokerLocationPatchInput,
  BrokerSettings,
  CreateProviderKeyInput,
  CreateProviderKeyResponse,
  CredentialShapeListResponse,
  InvitationPreview,
  InviteTeamMemberInput,
  Microsoft365ConnectResponse,
  PasswordChangeLinkResponse,
  PasswordResetConfirmInput,
  PatchDefaultModelInput,
  ProviderKeyListResponse,
  ProviderKeyRow,
  TeamMember,
  TeamMemberListResponse,
  TeamMemberPatchInput,
  TitleOptionsResponse,
  UsStatesResponse,
} from "./types";


export function fetchTeamMembers(opts?: {
  signal?: AbortSignal;
}): Promise<TeamMemberListResponse> {
  return api.get<TeamMemberListResponse>("/api/v1/team-members/", opts);
}


export function inviteTeamMember(
  input: InviteTeamMemberInput,
): Promise<TeamMember> {
  return api.post<TeamMember>("/api/v1/team-members/", input);
}


export function updateTeamMember(
  memberId: string,
  input: TeamMemberPatchInput,
): Promise<TeamMember> {
  return api.patch<TeamMember>(
    `/api/v1/team-members/${encodeURIComponent(memberId)}`,
    input,
  );
}


export function fetchTitleOptions(opts?: {
  signal?: AbortSignal;
}): Promise<TitleOptionsResponse> {
  return api.get<TitleOptionsResponse>(
    "/api/v1/team-members/titles/", opts,
  );
}


export function resendInvitation(invitationId: string): Promise<TeamMember> {
  return api.post<TeamMember>(
    `/api/v1/team-members/invitations/${encodeURIComponent(invitationId)}/resend`,
  );
}


export function revokeInvitation(invitationId: string): Promise<void> {
  return api.delete<void>(
    `/api/v1/team-members/invitations/${encodeURIComponent(invitationId)}`,
  );
}


export function fetchInvitationPreview(
  token: string,
  opts?: { signal?: AbortSignal },
): Promise<InvitationPreview> {
  return api.get<InvitationPreview>(
    `/api/v1/invitations/${encodeURIComponent(token)}`,
    opts,
  );
}


export function acceptInvitation(
  token: string,
  input: AcceptInvitationInput,
): Promise<unknown> {
  return api.post(
    `/api/v1/invitations/${encodeURIComponent(token)}/accept`,
    input,
  );
}


// ── Settings-02 — Broker Locations ───────────────────────────────────────


export function fetchBrokerLocations(opts?: {
  signal?: AbortSignal;
  includeArchived?: boolean;
}): Promise<BrokerLocationListResponse> {
  const qs = opts?.includeArchived ? "?include_archived=true" : "";
  return api.get<BrokerLocationListResponse>(
    `/api/v1/broker-locations/${qs}`,
    opts ? { signal: opts.signal } : undefined,
  );
}


export function createBrokerLocation(
  input: BrokerLocationCreateInput,
): Promise<BrokerLocation> {
  return api.post<BrokerLocation>("/api/v1/broker-locations/", input);
}


export function updateBrokerLocation(
  locationId: string,
  input: BrokerLocationPatchInput,
): Promise<BrokerLocation> {
  return api.patch<BrokerLocation>(
    `/api/v1/broker-locations/${encodeURIComponent(locationId)}`,
    input,
  );
}


export function archiveBrokerLocation(
  locationId: string,
): Promise<BrokerLocation> {
  return api.post<BrokerLocation>(
    `/api/v1/broker-locations/${encodeURIComponent(locationId)}/archive`,
    {},
  );
}


export function restoreBrokerLocation(
  locationId: string,
): Promise<BrokerLocation> {
  return api.post<BrokerLocation>(
    `/api/v1/broker-locations/${encodeURIComponent(locationId)}/restore`,
    {},
  );
}


// ── Settings-02 — Password change ────────────────────────────────────────


export function requestPasswordChangeLink(): Promise<PasswordChangeLinkResponse> {
  return api.post<PasswordChangeLinkResponse>(
    "/api/v1/auth/password-change-link",
    {},
  );
}


export function confirmPasswordReset(
  input: PasswordResetConfirmInput,
): Promise<{ detail: string }> {
  return api.post<{ detail: string }>(
    "/api/v1/auth/password-reset/confirm",
    input,
  );
}


// ── Integrations — Microsoft 365 OAuth ───────────────────────────────────


export function connectMicrosoft365(): Promise<Microsoft365ConnectResponse> {
  return api.get<Microsoft365ConnectResponse>(
    "/api/v1/integrations/microsoft/connect",
  );
}


// ── Settings-02b — US states reference ───────────────────────────────────


export function fetchUsStates(opts?: {
  signal?: AbortSignal;
}): Promise<UsStatesResponse> {
  return api.get<UsStatesResponse>("/api/v1/geo/states", opts);
}


// ── Settings-04 — LLM Provider Keys ──────────────────────────────────────


export function fetchBrokerSettings(
  entityId: string,
  opts?: { signal?: AbortSignal },
): Promise<BrokerSettings> {
  return api.get<BrokerSettings>(
    `/api/v1/entities/${encodeURIComponent(entityId)}/broker-settings`,
    opts,
  );
}


export function patchBrokerSettings(
  entityId: string,
  input: Partial<BrokerSettings>,
): Promise<BrokerSettings> {
  return api.patch<BrokerSettings>(
    `/api/v1/entities/${encodeURIComponent(entityId)}/broker-settings`,
    input,
  );
}


export function fetchProviderKeys(opts?: {
  signal?: AbortSignal;
}): Promise<ProviderKeyListResponse> {
  return api.get<ProviderKeyListResponse>(
    "/api/v1/agents/provider-keys/",
    opts,
  );
}


export function setDefaultProviderKey(
  providerKeyId: string,
): Promise<ProviderKeyRow> {
  return api.post<ProviderKeyRow>(
    `/api/v1/agents/provider-keys/${encodeURIComponent(providerKeyId)}/set-default`,
  );
}


export function fetchCredentialShapes(opts?: {
  signal?: AbortSignal;
}): Promise<CredentialShapeListResponse> {
  return api.get<CredentialShapeListResponse>(
    "/api/v1/agents/provider-keys/credential-shapes",
    opts,
  );
}


export function createProviderKey(
  input: CreateProviderKeyInput,
): Promise<CreateProviderKeyResponse> {
  return api.post<CreateProviderKeyResponse>(
    "/api/v1/agents/provider-keys/",
    input,
  );
}


export function fetchBeneAIAgent(opts?: {
  signal?: AbortSignal;
}): Promise<BeneAIAgentSummary> {
  return api.get<BeneAIAgentSummary>(
    "/api/v1/agents/beneai",
    opts,
  );
}


export function patchBeneAIDefaultModel(
  input: PatchDefaultModelInput,
): Promise<BeneAIAgentSummary> {
  return api.patch<BeneAIAgentSummary>(
    "/api/v1/agents/beneai/default-model",
    input,
  );
}
